'use client';

import type * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Spinner } from '@/shared/ui/spinner';
import { cn } from '@/shared/utils/cn';

const buttonVariants = cva(
  "inline-flex w-fit items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive cursor-pointer",
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-foreground shadow-xs',
        destructive:
          'bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        outline:
          'border border-ring bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
        secondary: 'bg-zinc-200 text-zinc-900 shadow-xs hover:bg-zinc-300',
        ghost:
          'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50 active:bg-gray-200 active:text-accent-foreground',
        link: 'text-primary hover:underline',
      },
      size: {
        xs: 'size-4',
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9',
      },
      flex: {
        true: 'w-full',
        false: 'w-fit',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  }
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  pending,
  flex,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    pending?: boolean;
    flex?: boolean;
  }) {
  const Comp = asChild ? Slot : 'button';

  const Label = (
    <>
      {pending && (
        <Spinner
          size={'sm'}
          variant={'secondary'}
        />
      )}
      {!pending && <>{props.children}</>}
    </>
  );

  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className, flex }), [
        pending && 'opacity-50 cursor-not-allowed',
      ])}
      data-slot="button"
      {...props}
    >
      {Label}
    </Comp>
  );
}

export { Button, buttonVariants };
